import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowRight, Globe2, Plus, Search, Download } from "lucide-react";
import { analyticsWebsites } from "../../api/analyticsWebsites";
import { Button } from "../../components/ui/Button/Button";
import Modal from "../../components/Modal/Modal";
import { useAnalyticsScope } from "../../features/analytics/useAnalyticsScope";
import {
  analyticsKey,
  type AnalyticsScope,
} from "../../features/analytics/queryKeys";
import { rangeParams } from "../../features/analytics/dateRange";
import {
  metricChange,
  metricValue,
  errorMessage,
} from "../../features/analytics/format";
import type {
  DateRange,
  Overview,
  Website,
} from "../../features/analytics/types";
import DateRangeControl from "../../features/analytics/components/DateRangeControl";
import AddWebsiteDialog from "../../features/analytics/components/AddWebsiteDialog";
import {
  Empty,
  ErrorState,
  Loading,
  SourceBadge,
} from "../../features/analytics/components/States";
import styles from "../../features/analytics/Analytics.module.css";
import { useAnalyticsDateRange } from "../../features/analytics/useAnalyticsDateRange";
export default function AnalyticsWebsites() {
  const scope = useAnalyticsScope();
  if (scope.error)
    return (
      <main className={styles.root}>
        <h1>Analytics</h1>
        <ErrorState error={new Error(scope.error)} retry={scope.retry} />
      </main>
    );
  if (!scope.ready)
    return (
      <main className={styles.root}>
        <Loading label="Loading workspace…" />
      </main>
    );
  return (
    <Collection
      key={`${scope.scope.userId}:${scope.scope.organizationId}`}
      scope={scope.scope}
      canManage={scope.canManage}
    />
  );
}
function Collection({
  scope,
  canManage,
}: {
  scope: AnalyticsScope;
  canManage: boolean;
}) {
  const { params, setParams, range } = useAnalyticsDateRange();
  const q = params.get("q") ?? "",
    page = Math.max(1, Number(params.get("page")) || 1);
  const [search, setSearch] = useState(q),
    [adding, setAdding] = useState(false),
    [importing, setImporting] = useState(false);
  const client = useQueryClient();
  const websites = useQuery({
    queryKey: analyticsKey(scope, "websites", "collection", { q, page }),
    queryFn: ({ signal }) =>
      analyticsWebsites.list({ q, page, page_size: 25 }, signal),
    staleTime: 30000,
  });
  const mutation = useMutation({
    mutationFn: analyticsWebsites.bootstrap,
    onSuccess: () =>
      client.invalidateQueries({ queryKey: analyticsKey(scope, "websites") }),
  });
  useEffect(() => {
    try {
      sessionStorage.setItem(
        `analytics-home:${scope.userId}:${scope.organizationId ?? "personal"}`,
        params.size ? `?${params}` : "",
      );
    } catch {
      /* The URL itself still preserves the selection. */
    }
  }, [params, scope]);
  useEffect(() => setSearch(q), [q]);
  const update = (values: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([k, v]) =>
      v ? next.set(k, v) : next.delete(k),
    );
    setParams(next);
  };
  return (
    <main className={styles.root}>
      <header className={styles.collectionHeader}>
        <div>
          <div className={styles.eyebrow}>ANALYTICS</div>
          <h1>Your websites</h1>
          <p>Search and traffic performance, together in one place.</p>
        </div>
        {canManage && (
          <div className={styles.actions}>
            <Button
              variant="ghost"
              icon={<Download size={16} />}
              onClick={() => {
                mutation.reset();
                setImporting(true);
              }}
            >
              Import connected websites
            </Button>
            <Button icon={<Plus size={17} />} onClick={() => setAdding(true)}>
              Add website
            </Button>
          </div>
        )}
      </header>
      <div className={styles.collectionToolbar}>
        <form
          className={styles.search}
          onSubmit={(e) => {
            e.preventDefault();
            update({ q: search.trim(), page: undefined });
          }}
        >
          <Search size={17} />
          <label htmlFor="website-search" className={styles.srOnly}>
            Search websites
          </label>
          <input
            id="website-search"
            placeholder="Search websites…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button variant="ghost" type="submit">
            Search
          </Button>
          {q && (
            <Button
              variant="ghost"
              onClick={() => {
                setSearch("");
                update({ q: undefined, page: undefined });
              }}
            >
              Clear
            </Button>
          )}
        </form>
        <span className={styles.collectionCount}>
          {websites.data?.total_available_rows ?? "…"} websites ·{" "}
          {scope.organizationId ? "Workspace" : "Personal"}
        </span>
      </div>
      <details className={styles.collectionDates}>
        <summary>
          Report period: {range.start} – {range.end}
        </summary>
        <DateRangeControl
          range={range}
          onChange={(next) => update({ ...next })}
        />
      </details>
      {websites.isPending ? (
        <Loading label="Loading websites…" />
      ) : websites.isError ? (
        <ErrorState error={websites.error} retry={() => websites.refetch()} />
      ) : !websites.data.items.length ? (
        <Empty
          title={q ? "No websites match your search" : "Add your first website"}
          action={
            canManage && !q ? (
              <Button icon={<Plus size={16} />} onClick={() => setAdding(true)}>
                Add website
              </Button>
            ) : undefined
          }
        >
          {q
            ? "Try a different name or clear the search."
            : "Create a website or import your existing connected resources. You can add Google Analytics, Search Console, or both."}
        </Empty>
      ) : (
        <section className={styles.collection} aria-label="Websites">
          <div className={styles.collectionColumns}>
            <span>Website</span>
            <span>Search clicks · GSC</span>
            <span>Sessions · GA4</span>
            <span>Connections</span>
            <span />
          </div>
          {websites.data.items.map((website) => (
            <WebsiteRow
              key={website.id}
              website={website}
              scope={scope}
              range={range}
            />
          ))}
          <div className={styles.pagination}>
            <span>
              Page {page} · {websites.data.total_available_rows} websites
            </span>
            <div>
              <Button
                variant="ghost"
                disabled={page === 1}
                onClick={() => update({ page: String(page - 1) })}
              >
                Previous
              </Button>
              <Button
                variant="ghost"
                disabled={
                  page * websites.data.page_size >=
                  websites.data.total_available_rows
                }
                onClick={() => update({ page: String(page + 1) })}
              >
                Next
              </Button>
            </div>
          </div>
        </section>
      )}
      {adding && (
        <AddWebsiteDialog
          scope={scope}
          range={range}
          onClose={() => setAdding(false)}
        />
      )}
      {importing && (
        <Modal
          isOpen
          onClose={() => setImporting(false)}
          title="Import connected websites"
          footer={
            <div className={styles.actions}>
              <Button
                variant="secondary"
                disabled={mutation.isPending}
                onClick={() => setImporting(false)}
              >
                {mutation.isSuccess ? "Done" : "Cancel"}
              </Button>
              {!mutation.isSuccess && (
                <Button
                  loading={mutation.isPending}
                  onClick={() => mutation.mutate()}
                >
                  Import websites
                </Button>
              )}
            </div>
          }
        >
          <div className={styles.formStack}>
            <p>
              Create website entries from the reporting resources already
              accessible in this workspace. Existing entries stay intact.
            </p>
            <p>
              Each resource is kept separate when its pairing cannot be
              verified. Review and combine sources from Connections when you
              know their exact scope.
            </p>
            {mutation.isSuccess && (
              <p role="status">
                Import complete. The website collection is up to date. Review
                any source pairings from Connections.
              </p>
            )}
            {mutation.isError && (
              <p role="alert">{errorMessage(mutation.error)}</p>
            )}
          </div>
        </Modal>
      )}
    </main>
  );
}
function WebsiteRow({
  website,
  scope,
  range,
}: {
  website: Website;
  scope: AnalyticsScope;
  range: DateRange;
}) {
  const summary = useQuery({
    queryKey: analyticsKey(
      scope,
      "collection-report",
      website.id,
      website.mapping_version,
      range,
    ),
    queryFn: ({ signal }) =>
      analyticsWebsites.report<Overview>(
        website.id,
        "overview",
        range,
        {},
        signal,
      ),
    enabled: !!(website.primary_ga4_property_id || website.primary_gsc_site_id),
    staleTime: 60000,
  });
  return (
    <article className={styles.websiteRow}>
      <div className={styles.websiteIdentity}>
        <div className={styles.siteIcon}>
          <Globe2 size={20} />
        </div>
        <div>
          <h2>
            <Link
              to={`/analytics/websites/${website.id}/overview?${rangeParams(range)}`}
            >
              {website.name}
            </Link>
          </h2>
          <p>{website.scope_value || "Scope not verified"}</p>
          {website.mapping_status !== "confirmed" && (
            <small>Review source mapping</small>
          )}
        </div>
      </div>
      {(["clicks", "sessions"] as const).map((metric) => (
        <div key={metric} className={styles.rowMetric}>
          <small>
            {metric === "clicks" ? "Search clicks · GSC" : "Sessions · GA4"}
          </small>
          <strong>
            {summary.isPending && summary.fetchStatus === "fetching"
              ? "…"
              : metricValue(summary.data?.payload.metrics[metric])}
          </strong>
          {range.compare === "previous" && summary.data && (
            <span>{metricChange(summary.data.payload.metrics[metric])}</span>
          )}
          {summary.isError && (
            <button
              className={styles.textLink}
              onClick={() => summary.refetch()}
            >
              Retry report
            </button>
          )}
        </div>
      ))}
      <div className={styles.rowSources}>
        <SourceBadge
          source="gsc"
          state={summary.data?.sources.gsc}
          connected={!!website.primary_gsc_site_id}
        />
        <SourceBadge
          source="ga4"
          state={summary.data?.sources.ga4}
          connected={!!website.primary_ga4_property_id}
        />
        {summary.isError && <small>Report unavailable</small>}
      </div>
      <Link
        className={styles.openLink}
        to={`/analytics/websites/${website.id}/overview?${rangeParams(range)}`}
      >
        Open analytics <ArrowRight size={16} />
      </Link>
    </article>
  );
}
